import type { BaseApiResponse } from '../../lib/flotiq-client/types/api-request';
import type { AllConstraints } from '../../lib/flotiq-client/types/api-response';
import type { HookOptions } from './hook-builders/types';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { getAllConstraints, getConstraints } from '../../lib/flotiq-client';
import { parseResponseErrors } from '../../lib/flotiq-client/response-errors';
import { getQueryCacheKey } from '../../lib/queryClientHelpers';

import useSelectedSpace from '../useSelectedSpace';
import useToken from '../useToken';

const DEFAULT_OPTIONS = {};

const allConstraintsMap = {
  'content-objects-count': 'cto-count',
  'content-definitions-count': 'ctd-count',
};

const CACHE_TIME = 5 * 60 * 1000; // 5 minutes

type ReloadFn = (constraintNames?: string[]) => void;

interface HookAllSpaceConstraintsObjectResult {
  allConstraints: AllConstraints | null;
  isLoading: boolean;
  errors: any;
  reload: ReloadFn;
}

type HookAllSpaceConstraintsResultArrayResult = [
  allConstraints: AllConstraints | null,
  isLoading: boolean,
  errors: any,
  reload: ReloadFn,
];

type HookAllSpaceConstraintsResult = HookAllSpaceConstraintsObjectResult &
  HookAllSpaceConstraintsResultArrayResult;

export const useAllSpaceConstraints = (
  space?: string,
  options: HookOptions = DEFAULT_OPTIONS,
) => {
  const queryClient = useQueryClient();
  const jwt = useToken();
  const { space: currentSpace } = useSelectedSpace();

  const {
    checkStatus = true,
    cancelUnfinishedRequests = true,
    pause = false,
    staleTime = CACHE_TIME,
    gcTime = CACHE_TIME * 2,
  } = options || {};

  const finalSpace = useMemo(
    () => space || currentSpace,
    [currentSpace, space],
  );

  const params = useMemo(
    () => ({
      space: finalSpace,
    }),
    [finalSpace],
  );

  const queryKey = useMemo(
    () => getQueryCacheKey('constraints', 'all', params, 'entity'),
    [params],
  );

  const {
    isLoading: requestLoading,
    data: response,
    error: allConstraintsErrors,
    refetch: reloadAllConstraints,
    isPlaceholderData,
  } = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const result = await getAllConstraints(
        jwt,
        undefined,
        params,
        cancelUnfinishedRequests ? { signal } : {},
      );
      if (checkStatus && !result.ok) {
        throw parseResponseErrors(result.body, result.status);
      }
      return result;
    },
    enabled: !!jwt && !pause && !!finalSpace,
    staleTime,
    gcTime,
    retry: 0,
  });

  const isLoading = requestLoading || isPlaceholderData;

  const allConstraints = useMemo(
    () => response?.body || null,
    [response?.body],
  );

  const reloadSelectedConstraints = useCallback(
    async (names: string[]) => {
      const newCounts: Record<string, number | string> = {};
      return Promise.all(
        names.map((name) =>
          getConstraints(jwt, undefined, {
            name:
              name in allConstraintsMap
                ? allConstraintsMap[name as keyof typeof allConstraintsMap]
                : name,
            space: finalSpace,
          })
            .then(({ body: constraintBody, status, ok }) => {
              if (!ok) {
                throw parseResponseErrors(constraintBody, status);
              }
              newCounts[name] = constraintBody.data;
            })
            .catch((e) => {
              console.error(e);
            }),
        ),
      )
        .then(() =>
          queryClient.setQueryData<BaseApiResponse<AllConstraints>>(
            queryKey,
            (oldData) =>
              oldData
                ? {
                    ...oldData,
                    body: { ...oldData?.body, ...newCounts },
                  }
                : { body: newCounts, ok: true, status: 200 },
          ),
        )
        .catch((e) => console.error(e));
    },
    [queryKey, finalSpace, jwt, queryClient],
  );

  const reload = useCallback<ReloadFn>(
    async (constraintNames) => {
      if (!constraintNames?.length) return reloadAllConstraints();
      return reloadSelectedConstraints(constraintNames);
    },
    [reloadAllConstraints, reloadSelectedConstraints],
  );

  const result = [allConstraints, isLoading, allConstraintsErrors, reload];
  Object.assign(result, {
    allConstraints,
    isLoading,
    errors: allConstraintsErrors,
    reload,
  });
  return result as HookAllSpaceConstraintsResult;
};
